import { useState, useRef } from 'react';
import { trpc } from '../../lib/trpc';
import { Label } from '../ui/label';
import { CustomButton } from '../uiWrappers/button';
import { AudioType, AudioFileExtension, QuestionType } from '@mightybyte/business';
import { toast } from 'sonner';
import { FiX } from 'react-icons/fi';
import { PiSpeakerHigh } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import { Progress } from '../ui/progress';

interface AudioUploadState {
  uploading: boolean;
  progress: number;
  error?: string;
}

export interface AudioEntry {
  url: string;      // public URL (for playback)
  transcript?: string;
}

interface AudioUploadSectionProps {
  questionType: QuestionType;
  value: Partial<Record<AudioType, AudioEntry>> | null;
  onChange: (value: Partial<Record<AudioType, AudioEntry>> | null) => void;
}

const audioTypeLabels: Record<AudioType, string> = {
  [AudioType.Correct]: 'Correct Answer Audio',
  [AudioType.Incorrect]: 'Incorrect Answer Audio',
  [AudioType.Underestimated]: 'Underestimated Answer Audio',
  [AudioType.Overestimated]: 'Overestimated Answer Audio',
};

export function AudioUploadSection({ questionType, value, onChange }: AudioUploadSectionProps) {
  const [uploadStates, setUploadStates] = useState<Record<AudioType, AudioUploadState>>({
    [AudioType.Correct]: { uploading: false, progress: 0 },
    [AudioType.Incorrect]: { uploading: false, progress: 0 },
    [AudioType.Underestimated]: { uploading: false, progress: 0 },
    [AudioType.Overestimated]: { uploading: false, progress: 0 },
  });

  const audioElementsRef = useRef<Record<AudioType, HTMLAudioElement | null>>({
    [AudioType.Correct]: null,
    [AudioType.Incorrect]: null,
    [AudioType.Underestimated]: null,
    [AudioType.Overestimated]: null,
  });

  const fileInputRefs = useRef<Record<AudioType, HTMLInputElement | null>>({
    [AudioType.Correct]: null,
    [AudioType.Incorrect]: null,
    [AudioType.Underestimated]: null,
    [AudioType.Overestimated]: null,
  });

  const getAudioUploadUrlMutation = trpc.admin.quiz.getAudioUploadUrl.useMutation();

  const handleAudioUpload = async (audioType: AudioType, file: File) => {
    try {
      setUploadStates(prev => ({
        ...prev,
        [audioType]: { uploading: true, progress: 0, error: undefined },
      }));

      // Get file extension
      const extension = file.name.split('.').pop()?.toLowerCase() as AudioFileExtension;
      if (!Object.values(AudioFileExtension).includes(extension)) {
        throw new Error('Invalid file type. Please upload MP3, WAV, M4A, or OGG files.');
      }

      // Get signed upload URL with original filename
      const uploadData = await getAudioUploadUrlMutation.mutateAsync({
        fileExtension: extension,
        originalFileName: file.name,
      });

      // Upload file to GCS
      const xhr = new XMLHttpRequest();

      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadStates(prev => ({
              ...prev,
              [audioType]: { ...prev[audioType], progress },
            }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open(uploadData.requestMethod, uploadData.signedUrl);

        Object.entries(uploadData.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });

        xhr.send(file);
      });

      // Preserve existing transcript if any
      const existingTranscript = value?.[audioType]?.transcript;

      onChange({
        ...(value || {}),
        [audioType]: {
          url: uploadData.publicUrl,
          ...(existingTranscript !== undefined && { transcript: existingTranscript }),
        },
      });

      setUploadStates(prev => ({
        ...prev,
        [audioType]: { uploading: false, progress: 100 },
      }));

      toast.success('Audio uploaded successfully!');
    } catch (error) {
      console.error('Audio upload error:', error);
      setUploadStates(prev => ({
        ...prev,
        [audioType]: {
          uploading: false,
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }));
      toast.error('Failed to upload audio');
    }
  };

  const handleFileSelect = (audioType: AudioType, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        toast.error('Please select an audio file');
        return;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Audio size must be less than 50MB');
        return;
      }

      void handleAudioUpload(audioType, file);
    }
    // Reset input value so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveAudio = (audioType: AudioType) => {
    const newValue = { ...(value || {}) };
    delete newValue[audioType];
    onChange(Object.keys(newValue).length > 0 ? newValue : null);

    // Stop audio if playing
    if (audioElementsRef.current[audioType]) {
      audioElementsRef.current[audioType]!.pause();
      audioElementsRef.current[audioType]!.currentTime = 0;
    }
  };

  const handleTranscriptChange = (audioType: AudioType, transcript: string) => {
    const entry = value?.[audioType];
    if (!entry) return;
    onChange({
      ...(value || {}),
      [audioType]: {
        ...entry,
        transcript: transcript || undefined,
      },
    });
  };

  const triggerFileInput = (audioType: AudioType) => {
    fileInputRefs.current[audioType]?.click();
  };

  // Extract original filename from UUID-prefixed filename or URL
  const getDisplayFileName = (fileNameOrUrl: string): string => {
    let fileName = fileNameOrUrl;

    // If it's a URL, extract the filename from the end
    if (fileNameOrUrl.startsWith('https://') || fileNameOrUrl.startsWith('http://')) {
      const urlParts = fileNameOrUrl.split('/');
      fileName = urlParts[urlParts.length - 1];
    }

    // Filename format: {uuid}_{original-name}.{ext}
    // Extract the part after the first underscore
    const parts = fileName.split('_');
    if (parts.length > 1) {
      // Remove UUID prefix and join the rest
      return parts.slice(1).join('_');
    }
    return fileName;
  };

  // Determine which audio types to show based on question type
  const displayedAudioTypes = (() => {
    switch (questionType) {
      case QuestionType.Boolean:
        return [AudioType.Correct, AudioType.Incorrect];
      case QuestionType.Numeric:
      case QuestionType.Percentage:
        return [AudioType.Correct, AudioType.Underestimated, AudioType.Overestimated];
      case QuestionType.SingleChoice:
        return [AudioType.Correct];
      default:
        return [AudioType.Correct];
    }
  })();

  return (
    <div className="space-y-3 pb-4 border-b">
      <h3 className="text-base font-bold text-foreground tracking-wide">Upload Audios</h3>

      {displayedAudioTypes.map((audioType) => {
        const uploadState = uploadStates[audioType];
        const entry = value?.[audioType];
        const audioUrl = entry?.url;

        return (
          <div key={audioType} className="space-y-2">
            <Label>{audioTypeLabels[audioType]}</Label>

            <input
              ref={(el) => (fileInputRefs.current[audioType] = el)}
              type="file"
              accept="audio/mp3,audio/wav,audio/mp4,audio/ogg"
              onChange={(e) => handleFileSelect(audioType, e)}
              className="hidden"
              disabled={uploadState.uploading}
            />

            {uploadState.uploading && <Progress value={uploadState.progress} />}

            {uploadState.error && (
              <p className="text-sm text-destructive">{uploadState.error}</p>
            )}

            {audioUrl && !uploadState.uploading ? (
              <div className="relative p-3 border border-border rounded-lg bg-muted/30">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 pr-8">
                    <PiSpeakerHigh className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {getDisplayFileName(audioUrl)}
                    </span>
                  </div>
                  <audio
                    ref={(el) => {
                      audioElementsRef.current[audioType] = el;
                    }}
                    src={audioUrl}
                    controls
                    className="w-full h-9"
                    style={{ maxWidth: '100%' }}
                  />
                  <div className="mt-1">
                    <Label className="text-xs text-muted-foreground">Transcript (optional)</Label>
                    <textarea
                      value={entry?.transcript ?? ''}
                      onChange={(e) => handleTranscriptChange(audioType, e.target.value)}
                      rows={3}
                      placeholder="Add transcript text for this audio..."
                      className={cn(
                        'w-full resize-none mt-1 px-3 py-2 text-sm border border-input rounded-lg bg-transparent',
                        'focus-visible:outline-none focus-visible:ring-0.5 focus-visible:ring-brand focus-visible:border-ring',
                        'placeholder:text-muted-foreground transition-colors',
                      )}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAudio(audioType)}
                  className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1.5 hover:bg-destructive/90 shadow-md"
                  aria-label="Remove audio"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </div>
            ) : !uploadState.uploading ? (
              <CustomButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => triggerFileInput(audioType)}
                className="w-full"
              >
                Choose File
              </CustomButton>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}